import React from 'react';
import { motion } from 'framer-motion';
import { useGentleMotion as useReducedMotion } from '../hooks/useGentleMotion';
import { ArrowRightIcon } from 'lucide-react';
import { Reveal } from './Reveal';
const before = {
  steps: ['Buscar', 'Guardar', 'Organizar', 'Improvisar']
};
interface BeforeAfterProps {
  beforeImage: string;
  afterImage: string;
}
export function BeforeAfter({
  beforeImage,
  afterImage
}: BeforeAfterProps) {
  const reduce = useReducedMotion();
  return <section className="w-full bg-white py-16 sm:py-20" aria-labelledby="transform-title" data-id="element-39" data-mp="3jgojzmgqxuz5">
      <div className="mx-auto max-w-content px-5 sm:px-8" data-id="element-40" data-mp="34rsbxpeowhyj">
        <Reveal data-id="element-41" data-mp="34td5pct6jwvc">
          <h2 id="transform-title" className="mx-auto max-w-3xl text-center font-display text-2xl font-extrabold uppercase leading-tight text-navy sm:text-4xl" data-id="element-42" data-mp="34ppbscjtp8g3">
            De buscar qué hacer...
            <br data-id="element-43" data-mp="30pxa8jsuk2k8" />
            <span className="text-brandgreen" data-id="element-44" data-mp="30qgi17os9akj">a tener la idea preparada.</span>
          </h2>
        </Reveal>

        <Reveal delay={0.1} data-id="element-45" data-mp="x3isovuyzbp3">
          <div className="relative mt-10 grid grid-cols-1 overflow-hidden rounded-3xl border border-navy/10 shadow-card md:grid-cols-2" data-id="element-46" data-mp="x2qwbitjt9xr">
            {/* Animated hand-off between the two states */}
            <motion.span className="absolute left-1/2 top-1/2 z-10 hidden h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-card md:flex" animate={reduce ? undefined : {
            x: [-6, 6, -6]
          }} transition={{
            duration: 2.4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} aria-hidden="true" data-id="element-47" data-mp="171yi8uecmcwh">
              <ArrowRightIcon className="h-5 w-5 text-brandgreen" data-id="element-48" data-mp="o56r5yskethv" />
            </motion.span>

            {/* ANTES */}
            <div className="bg-cream2 p-6 sm:p-8" data-id="element-49" data-mp="jbc9f0wisolf">
              <span className="inline-flex rounded-full bg-white px-3 py-1 font-display text-[11px] font-extrabold uppercase tracking-[0.16em] text-muted" data-id="element-50" data-mp="3rrxe45i4cviq">
                Antes
              </span>
              <div className="mt-5 overflow-hidden rounded-2xl bg-white" data-id="element-51" data-mp="1qpogm83dsxml">
                <img src={beforeImage} alt="Adulto buscando actividades en el celular con materiales sobre la mesa" className="h-52 w-full object-cover sm:h-60" width={1264} height={843} loading="lazy" decoding="async" data-id="element-52" data-mp="1vpapy6g39tq4" />
              </div>
              <div className="mt-5 flex flex-wrap gap-2" data-id="element-53" data-mp="1cqyj4rcngvxp">
                {before.steps.map(s => <span key={s} className="rounded-lg bg-white px-2.5 py-1 font-display text-[11px] font-extrabold uppercase tracking-wide text-muted" data-id="element-54" data-mp="1gstg5jnjsn5o">
                    {s}
                  </span>)}
              </div>
              <p className="mt-5 font-sans text-sm leading-relaxed text-muted" data-id="element-55" data-mp="2x6tggvqb2etg">
                Buscar ideas cada vez, revisar materiales e improvisar.
              </p>
            </div>

            {/* DESPUÉS */}
            <div className="bg-mint p-6 sm:p-8" data-id="element-56" data-mp="32b5cbp3o4j2a">
              <span className="inline-flex rounded-full bg-brandgreen px-3 py-1 font-display text-[11px] font-extrabold uppercase tracking-[0.16em] text-white" data-id="element-57" data-mp="32cf98aysbl7c">
                Con El Gran Reto Creativo
              </span>
              <div className="mt-5 overflow-hidden rounded-2xl bg-white" data-id="element-58" data-mp="2ok8v0uf6zoaj">
                <img src={afterImage} alt="Adulto y niño haciendo juntos una actividad creativa sencilla" className="h-52 w-full object-cover sm:h-60" width={1264} height={843} loading="lazy" decoding="async" data-id="element-59" data-mp="2oljw2mh96hxy" />
              </div>
              <div className="mt-5 flex flex-wrap gap-2" data-id="element-60" data-mp="cteoq0509oob">
                {['Abrir', 'Elegir', 'Preparar', 'Crear'].map(s => <span key={s} className="rounded-lg bg-white px-2.5 py-1 font-display text-[11px] font-extrabold uppercase tracking-wide text-brandgreen" data-id="element-61" data-mp="8r03xahs0eue">
                    {s}
                  </span>)}
              </div>
              <p className="mt-5 font-sans text-sm font-medium leading-relaxed text-navy" data-id="element-62" data-mp="6xpasuwnsv6h">
                Abres la guía, eliges una actividad y empiezan a crear.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15} data-id="element-63" data-mp="1w9shalv92rs">
          <p className="mt-12 text-center font-display text-[1.75rem] font-extrabold uppercase leading-[1.1] text-navy sm:text-5xl" data-id="element-64" data-mp="1xbdo7wlj5n8">
            Menos tiempo buscando.
            <br data-id="element-65" data-mp="3obl04bgyu3ld" />
            <span className="text-coral" data-id="element-66" data-mp="3obcjq9lmwg5f">Más tiempo haciendo.</span>
          </p>
        </Reveal>
      </div>
    </section>;
}